/* lib/useCtaTarget.js · per-location "Open Account" CTA routing.
 *
 * Returns the right href / target / rel / data-attributes for a given
 * CTA location based on:
 *
 *   1. The global wizard kill-switch (`signupWizardEnabled`)
 *      · When OFF, every location is forced to the direct CRM link
 *        regardless of per-location config. Use this when the CRM
 *        is in maintenance or the wizard captcha is failing.
 *
 *   2. The per-location admin override (`ctaRouting[locationKey]`)
 *      · Returns "wizard" (in-app modal) or "crm" (direct CRM link).
 *
 *   3. Built-in defaults — only `navbar_desktop` defaults to wizard,
 *      every other known location defaults to crm.
 *
 * Usage:
 *
 *   const cta = useCtaTarget("hero");
 *   <a {...cta.linkProps} className="btn-primary">Open account</a>
 *
 * `linkProps` always includes { href, target, rel } so the consuming
 * <a> can be a drop-in replacement for any existing `<a href={REGISTER_URL}
 * target="_blank" rel="noreferrer">` pattern.
 */

import { useMemo } from "react";
import { useSiteSettings } from "@/contexts/SiteSettingsContext";
import { REGISTER_URL, SIGNUP_MODAL_HREF } from "@/data/site";

// Built-in defaults · ONLY navbar_desktop defaults to wizard. Every
// other CTA on the site defaults to a direct CRM link, matching the
// historical broker-site behaviour. Admin can flip any of these to
// wizard from /admin/dashboard → Settings → CTA Routing.
const DEFAULT_ROUTING = {
  navbar_desktop:      "wizard",
  navbar_mobile:       "crm",
  hero:                "crm",
  cta_banner:          "crm",
  account_types:       "crm",
  account_comparison:  "crm",
  account_detail:      "crm",
  funding:             "crm",
  first_deposit_bonus: "crm",
  leverage:            "crm",
  onboarding_steps:    "crm",
  symbol_specs:        "crm",
};

// Human-readable labels for the admin UI · exported so the Settings
// tab can iterate over the same list as the helper itself, keeping
// the two surfaces in lock-step.
export const CTA_LOCATIONS = [
  { key: "navbar_desktop",      label: "Header (Desktop)",          group: "Header"        },
  { key: "navbar_mobile",       label: "Header (Mobile)",           group: "Header"        },
  { key: "hero",                label: "Homepage Hero",             group: "Homepage"      },
  { key: "cta_banner",          label: "Homepage CTA Banner",       group: "Homepage"      },
  { key: "account_types",       label: "Account Types section",     group: "Account pages" },
  { key: "account_comparison",  label: "Account Comparison page",   group: "Account pages" },
  { key: "account_detail",      label: "Account Type detail pages", group: "Account pages" },
  { key: "funding",             label: "Funding page",              group: "Specialty"     },
  { key: "first_deposit_bonus", label: "First Deposit Bonus page",  group: "Specialty"     },
  { key: "leverage",            label: "Leverage page",             group: "Specialty"     },
  { key: "onboarding_steps",    label: "Onboarding Steps section",  group: "Inline"        },
  { key: "symbol_specs",        label: "Symbol Specs drawer",       group: "Inline"        },
];

export function resolveCtaMode(locationKey, ctaRouting, signupWizardEnabled) {
  // Master kill-switch · CRM-only when the wizard is globally disabled.
  if (!signupWizardEnabled) return "crm";
  const override = ctaRouting && ctaRouting[locationKey];
  if (override === "wizard" || override === "crm") return override;
  return DEFAULT_ROUTING[locationKey] || "crm";
}

export function useCtaTarget(locationKey) {
  const { signupWizardEnabled, ctaRouting } = useSiteSettings();
  return useMemo(() => {
    const mode = resolveCtaMode(locationKey, ctaRouting, signupWizardEnabled);
    if (mode === "wizard") {
      return {
        mode,
        href: SIGNUP_MODAL_HREF,
        isExternal: false,
        // Spread-friendly props for an <a> · target/rel intentionally
        // omitted because the wizard modal is in-app · same-origin
        // navigation, no new tab.
        linkProps: { href: SIGNUP_MODAL_HREF },
      };
    }
    return {
      mode,
      href: REGISTER_URL,
      isExternal: true,
      linkProps: { href: REGISTER_URL, target: "_blank", rel: "noopener noreferrer" },
    };
  }, [locationKey, ctaRouting, signupWizardEnabled]);
}

export default useCtaTarget;
